import createClient, { type Middleware } from 'openapi-fetch';

import { session } from '@/lib/auth/session';
import { env } from '@/lib/env';

import type { paths } from './schema';

/** Error envelope of backend 2.0: { error: { code, message, request_id, details } }. */
export interface ApiErrorBody {
  error?: { code?: string; message?: string; request_id?: string; details?: unknown };
}

export class ApiError extends Error {
  readonly status: number;
  readonly code: string;
  readonly requestId: string | undefined;
  readonly details: unknown;

  constructor(status: number, body: ApiErrorBody | undefined, fallback: string) {
    super(body?.error?.message ?? fallback);
    this.name = 'ApiError';
    this.status = status;
    this.code = body?.error?.code ?? `HTTP_${String(status)}`;
    this.requestId = body?.error?.request_id;
    this.details = body?.error?.details;
  }
}

/** Thrown when the request never got a response: offline, DNS, CORS, timeout. */
export class NetworkError extends Error {
  constructor(cause: unknown) {
    super('Could not reach the server', { cause });
    this.name = 'NetworkError';
  }
}

const AUTH_PATH = '/api/v2/auth/';
const REFRESH_PATH = '/api/v2/auth/refresh';

/** Attach the bearer token and the org and role headers backend 2.0 expects. */
const auth: Middleware = {
  async onRequest({ request }) {
    const path = new URL(request.url).pathname;
    const isPublicAuth = path.startsWith(AUTH_PATH) && !path.endsWith('/me') && !path.includes('/me/');
    const { accessToken, orgId, roleId } = session.get();
    // Renew a token that is about to expire instead of waiting for the 401.
    const token = accessToken && session.isStale() && !isPublicAuth ? await session.refresh() : accessToken;
    if (token) request.headers.set('Authorization', `Bearer ${token}`);
    if (orgId) request.headers.set('X-Org-Id', orgId);
    if (roleId) request.headers.set('X-Role-Id', roleId);
    return request;
  },
  async onResponse({ request, response }) {
    const path = new URL(request.url).pathname;
    if (response.status !== 401 || path === REFRESH_PATH || !request.headers.has('Authorization'))
      return response;
    // One silent refresh from the httpOnly cookie, then one retry.
    const refreshed = await session.refresh();
    if (!refreshed) {
      session.clear();
      return response;
    }
    const retry = new Request(request, { headers: new Headers(request.headers) });
    retry.headers.set('Authorization', `Bearer ${refreshed}`);
    return fetch(retry);
  },
};

export const api = createClient<paths>({
  baseUrl: env.VITE_API_BASE,
  credentials: 'include', // the refresh cookie travels on the same site
  fetch: async (input) => {
    try {
      return await fetch(input);
    } catch (e: unknown) {
      throw new NetworkError(e);
    }
  },
});

api.use(auth);

/**
 * Unwrap an openapi-fetch result: return data or throw ApiError.
 * Every query hook in features/<area>/api.ts goes through this.
 */
export function unwrap<T>(result: { data?: T; error?: unknown; response: Response }): T {
  if (result.response.ok && result.data !== undefined) return result.data;
  if (result.response.ok) return result.data as T;
  const body = (result.error ?? undefined) as ApiErrorBody | undefined;
  throw new ApiError(
    result.response.status,
    body,
    `Request failed with status ${String(result.response.status)}`,
  );
}

/** For 202/204 routes that answer with no body. */
export function unwrapVoid(result: { error?: unknown; response: Response }): void {
  if (result.response.ok) return;
  const body = (result.error ?? undefined) as ApiErrorBody | undefined;
  throw new ApiError(
    result.response.status,
    body,
    `Request failed with status ${String(result.response.status)}`,
  );
}
